// Estado falso de Autorização e da trilha de auditoria (modo fake, só em dev — Parte 0.6; GIY-056): catálogo de papéis e
// permissões (subconjunto do Anexo A), matriz v1 publicada + rascunhos, atribuições (semeadas a partir de `/me`),
// regras de SoD, break-glass, campanha de recertificação, aprovações de duas chaves e eventos de auditoria, no formato
// do contrato (`Authorization*Response`, `AuditEventDto`). Semeado por cenário de `/me`.
import type { MeResponse, components } from '@giyus/api-client';

import { FIXTURE_IDS } from '../auth/me.fixture';

type Schemas = components['schemas'];
export type RoleResponse = Schemas['AuthorizationRoleResponse'];
export type PermissionResponse = Schemas['AuthorizationPermissionResponse'];
export type MatrixVersionResponse = Schemas['AuthorizationMatrixVersionResponse'];
export type MatrixVersionSummaryResponse = Schemas['AuthorizationMatrixVersionSummaryResponse'];
export type RoleAssignmentResponse = Schemas['AuthorizationRoleAssignmentResponse'];
export type SodRuleResponse = Schemas['AuthorizationSodRuleResponse'];
export type BreakGlassEventResponse = Schemas['AuthorizationBreakGlassEventResponse'];
export type AccessReviewCampaignResponse = Schemas['AuthorizationAccessReviewCampaignResponse'];
export type AccessReviewItemResponse = Schemas['AuthorizationAccessReviewItemResponse'];
export type TwoKeyApprovalResponse = Schemas['AuthorizationTwoKeyApprovalResponse'];
export type AuditEventResponse = Schemas['AuditEventDto'];

export interface FakeMatrixVersion extends Omit<MatrixVersionResponse, 'roles'> {
  version: number;
  roles: { roleCode: string; permissions: string[] }[];
  rowVersion: number;
}

export interface FakeAssignment extends Omit<RoleAssignmentResponse, 'status'> {
  /** Vigência avaliada na data corrente ao serializar. */
  readonly seeded: boolean;
}

export interface FakeSodRule extends SodRuleResponse {
  rowVersion: number;
  /** Alteração pendente (encerramento) aguardando a segunda chave. */
  pendingEnd: { reason: string } | null;
}

export interface FakeTwoKeyApproval extends TwoKeyApprovalResponse {
  readonly firstActorRoles: readonly string[];
  readonly payload: unknown;
}

export interface FakeAuthzState {
  readonly roles: RoleResponse[];
  readonly permissions: PermissionResponse[];
  readonly matrixVersions: FakeMatrixVersion[];
  readonly assignments: FakeAssignment[];
  readonly sodRules: FakeSodRule[];
  readonly breakGlass: BreakGlassEventResponse[];
  readonly campaigns: AccessReviewCampaignResponse[];
  readonly reviewItems: AccessReviewItemResponse[];
  readonly approvals: FakeTwoKeyApproval[];
  readonly audit: AuditEventResponse[];
  readonly base: string;
  counter: number;
  sequence: number;
}

export const AUTHZ_IDS = {
  matrixV1: '01926f3e-0000-7000-8000-0000000000m1',
  matrixDraft: '01926f3e-0000-7000-8000-0000000000m2',
  userQuality: '01926f3e-0000-7000-8000-0000000000u5',
  userDpo: '01926f3e-0000-7000-8000-0000000000u6',
  userInfosec: '01926f3e-0000-7000-8000-0000000000u7',
  userPilotAdmin: '01926f3e-0000-7000-8000-0000000000d2',
  userPilotPi: '01926f3e-0000-7000-8000-0000000000d3',
  studyPilot: FIXTURE_IDS.studyPilot,
  breakGlassPending: '01926f3e-0000-7000-8000-0000000000b1',
  breakGlassReviewed: '01926f3e-0000-7000-8000-0000000000b2',
  campaignOpen: '01926f3e-0000-7000-8000-0000000000c1',
  reviewItemMine: '01926f3e-0000-7000-8000-0000000000r1',
  reviewItemOther: '01926f3e-0000-7000-8000-0000000000r2',
  sodCalibration: '01926f3e-0000-7000-8000-00000000sd01',
  sodQuality: '01926f3e-0000-7000-8000-00000000sd02',
  sodDocument: '01926f3e-0000-7000-8000-00000000sd03',
} as const;

const ROLES: readonly RoleResponse[] = [
  role('HOLDING_ADMIN', 'Administrador da holding', 'Technical', ['Network'], { technical: true }),
  role('QUALITY_LEAD', 'Líder de qualidade', 'Quality', ['Network']),
  role('INFOSEC_LEAD', 'Segurança da informação', 'Technical', ['Network'], { technical: true }),
  role('DPO', 'Encarregado de dados', 'Privacy', ['Network']),
  role('PEOPLE_ADMIN', 'Administração de pessoas', 'Operational', ['Network']),
  role('STARTUP_SPECIALIST', 'Especialista de startup', 'Operational', ['Network', 'Study']),
  role('TENANT_ADMIN', 'Administrador do tenant', 'Technical', ['Tenant'], { technical: true }),
  role('SITE_OPERATIONS_LEAD', 'Líder de operações do site', 'Operational', ['Tenant', 'Site']),
  role('PRINCIPAL_INVESTIGATOR', 'Investigador principal', 'Clinical', ['Study']),
  role('CLINICAL_REVIEWER', 'Revisor clínico', 'Clinical', ['Study']),
  role('STUDY_COORDINATOR', 'Coordenador de estudo', 'Operational', ['Tenant', 'Site', 'Study']),
  role('SCHEDULER_RECEPTION', 'Recepção', 'Operational', ['Tenant', 'Site']),
  role('LOCAL_QUALITY', 'Qualidade local', 'Quality', ['Tenant']),
  role('EXTERNAL_AUDITOR', 'Auditor externo', 'External', ['Tenant', 'Study'], { external: true }),
];

const PERMISSIONS: readonly PermissionResponse[] = [
  perm('network.read', 'Painel da rede', ['Network']),
  perm('tenant.read.network', 'Tenants (rede)', ['Network']),
  perm('tenant.create', 'Criar tenant', ['Network']),
  perm('tenant.lifecycle.approve', 'Ciclo de vida do tenant', ['Network'], { stepUp: true }),
  perm('authz.read', 'Catálogo, matriz e regras', ['Network']),
  perm('authz.matrix.edit', 'Editar rascunho da matriz', ['Network']),
  perm('authz.matrix.publish', 'Publicar matriz (duas chaves)', ['Network'], { stepUp: true }),
  perm('authz.assignment.read', 'Ler atribuições', ['Network']),
  perm('authz.role.assign', 'Atribuir papéis', ['Network'], { stepUp: true }),
  perm('authz.assignment.read.tenant', 'Ler atribuições do tenant', ['Tenant']),
  perm('authz.role.assign.tenant', 'Atribuir papéis no tenant', ['Tenant'], { stepUp: true }),
  perm('authz.sod.manage', 'Regras de SoD (duas chaves)', ['Network'], { stepUp: true }),
  perm('authz.history.read', 'Point-in-time', ['Network'], { purpose: true }),
  perm('breakglass.use', 'Abrir break-glass', ['Tenant'], { stepUp: true }),
  perm('breakglass.review', 'Revisar break-glass', ['Network']),
  perm('access.review.read', 'Campanhas de recertificação', ['Network']),
  perm('access.review.decide', 'Decidir recertificação', ['Network', 'Tenant']),
  perm('audit.read', 'Consulta de auditoria', ['Tenant', 'StudyScoped'], { purpose: true }),
  perm('audit.read.network', 'Consulta de auditoria (rede)', ['Network'], { purpose: true }),
  perm('audit.export', 'Exportação de auditoria', ['Network', 'Tenant'], {
    purpose: true,
    stepUp: true,
  }),
  perm('site.read', 'Ler sites', ['Tenant']),
  perm('site.manage', 'Gerir sites', ['Tenant']),
  perm('participant.read', 'Ler participantes', ['StudyScoped']),
  perm('participant.identifiers.read', 'Identificadores de participante', ['StudyScoped'], {
    purpose: true,
  }),
  perm('person.sensitive.read', 'Dados sensíveis de pessoa', ['Network', 'Tenant'], {
    purpose: true,
  }),
  perm('equipment.calibration.record', 'Registrar calibração', ['Tenant']),
  perm('equipment.calibration.verify', 'Verificar calibração', ['Tenant']),
  perm('document.read', 'Ler documentos', ['Network', 'Tenant', 'StudyScoped']),
  perm('document.approve', 'Aprovar documentos', ['StudyScoped']),
  perm('study.read', 'Ler estudos', ['StudyScoped']),
];

const MATRIX_V1: readonly { roleCode: string; permissions: string[] }[] = [
  entry('HOLDING_ADMIN', [
    'network.read',
    'tenant.read.network',
    'tenant.create',
    'tenant.lifecycle.approve',
    'authz.read',
    'authz.assignment.read',
    'authz.role.assign',
    'authz.history.read',
    'audit.read.network',
  ]),
  entry('QUALITY_LEAD', [
    'authz.read',
    'authz.matrix.edit',
    'authz.matrix.publish',
    'authz.sod.manage',
    'authz.history.read',
    'access.review.read',
    'breakglass.review',
    'audit.read.network',
    'audit.export',
    'document.read',
  ]),
  entry('INFOSEC_LEAD', [
    'authz.read',
    'authz.sod.manage',
    'authz.history.read',
    'breakglass.review',
    'audit.read.network',
  ]),
  entry('DPO', [
    'authz.matrix.publish',
    'breakglass.review',
    'audit.read.network',
    'audit.export',
    'person.sensitive.read',
    'document.read',
  ]),
  entry('PEOPLE_ADMIN', ['access.review.decide']),
  entry('STARTUP_SPECIALIST', ['tenant.read.network', 'site.read', 'document.read', 'study.read']),
  entry('TENANT_ADMIN', [
    'site.read',
    'site.manage',
    'authz.assignment.read.tenant',
    'authz.role.assign.tenant',
    'access.review.decide',
    'audit.read',
    'equipment.calibration.record',
    'document.read',
  ]),
  entry('SITE_OPERATIONS_LEAD', [
    'site.read',
    'site.manage',
    'equipment.calibration.record',
    'equipment.calibration.verify',
    'document.read',
  ]),
  entry('PRINCIPAL_INVESTIGATOR', [
    'study.read',
    'participant.read',
    'participant.identifiers.read',
    'document.read',
    'document.approve',
    'audit.read',
    'breakglass.use',
  ]),
  entry('CLINICAL_REVIEWER', ['study.read', 'document.read']),
  entry('STUDY_COORDINATOR', [
    'study.read',
    'participant.read',
    'participant.identifiers.read',
    'document.read',
    'audit.read',
  ]),
  entry('SCHEDULER_RECEPTION', ['site.read']),
  entry('LOCAL_QUALITY', ['audit.read', 'document.read']),
  entry('EXTERNAL_AUDITOR', ['document.read', 'audit.read', 'study.read']),
];

function role(
  code: string,
  name: string,
  roleClass: string,
  allowedScopeTypes: string[],
  flags: { technical?: boolean; external?: boolean } = {},
): RoleResponse {
  return {
    code,
    name,
    class: roleClass,
    allowedScopeTypes,
    isExternal: flags.external ?? false,
    isTechnical: flags.technical ?? false,
  };
}

function perm(
  code: string,
  description: string,
  classes: string[],
  flags: { purpose?: boolean; stepUp?: boolean } = {},
): PermissionResponse {
  return {
    code,
    description,
    classes,
    requiresPurpose: flags.purpose ?? false,
    requiresStepUp: flags.stepUp ?? false,
    networkGrantable: classes.includes('Network'),
  };
}

function entry(roleCode: string, permissions: string[]) {
  return { roleCode, permissions: [...permissions].sort() };
}

export function fakeAuthzId(state: FakeAuthzState): string {
  state.counter += 1;
  return `01926f3e-0000-7000-8000-000000a${state.counter.toString(16).padStart(4, '0')}`;
}

export function shiftHours(base: string, hours: number): string {
  return new Date(new Date(base).getTime() + hours * 3_600_000).toISOString();
}

export function shiftDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days))
    .toISOString()
    .slice(0, 10);
}

export function todayUtc(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function assignmentStatusOn(assignment: FakeAssignment, date: string): string {
  if (assignment.revokedAt) {
    return 'Revoked';
  }
  if (assignment.effectiveFrom > date) {
    return 'Scheduled';
  }
  if (assignment.effectiveTo && assignment.effectiveTo < date) {
    return 'Expired';
  }
  return 'Active';
}

export function toAssignmentResponse(
  assignment: FakeAssignment,
  date: string,
): RoleAssignmentResponse {
  const { seeded: _seeded, ...rest } = assignment;
  return { ...rest, status: assignmentStatusOn(assignment, date) };
}

export function toMatrixSummary(version: FakeMatrixVersion): MatrixVersionSummaryResponse {
  return {
    id: version.id,
    version: version.version,
    status: version.status,
    effectiveFrom: version.effectiveFrom,
    effectiveTo: version.effectiveTo,
    publishedBy: version.publishedBy,
    secondKeyBy: version.secondKeyBy,
    changeSummary: version.changeSummary,
    twoKeyApprovalId: version.twoKeyApprovalId,
    createdAt: version.createdAt,
    entryCount: version.roles.reduce((total, item) => total + item.permissions.length, 0),
  };
}

export function toMatrixResponse(version: FakeMatrixVersion): MatrixVersionResponse {
  const { rowVersion: _rowVersion, ...rest } = version;
  return {
    ...rest,
    roles: version.roles.map((item) => ({ ...item, permissions: [...item.permissions] })),
  };
}

export function toSodRuleResponse(rule: FakeSodRule): SodRuleResponse {
  const { rowVersion: _rowVersion, pendingEnd: _pendingEnd, ...rest } = rule;
  return rest;
}

export function toApprovalResponse(approval: FakeTwoKeyApproval): TwoKeyApprovalResponse {
  const { firstActorRoles: _roles, payload: _payload, ...rest } = approval;
  return rest;
}

export function etagOf(rowVersion: number): string {
  return `W/"${rowVersion.toString(16).padStart(16, '0')}"`;
}

/** Papéis de rede do usuário (fonte: atribuições vigentes de escopo `Network`). */
export function networkRolesOf(state: FakeAuthzState, userId: string, date: string): string[] {
  return state.assignments
    .filter(
      (a) =>
        a.userId === userId &&
        a.scopeType === 'Network' &&
        assignmentStatusOn(a, date) === 'Active',
    )
    .map((a) => a.roleCode);
}

export function tenantRolesOf(
  state: FakeAuthzState,
  userId: string,
  tenantId: string,
  date: string,
): string[] {
  return state.assignments
    .filter(
      (a) =>
        a.userId === userId && a.tenantId === tenantId && assignmentStatusOn(a, date) === 'Active',
    )
    .map((a) => a.roleCode);
}

export function publishedMatrix(state: FakeAuthzState): FakeMatrixVersion | undefined {
  return state.matrixVersions.find((version) => version.status === 'Published');
}

/** Permissões de um conjunto de papéis pela versão publicada. */
export function permissionsOf(state: FakeAuthzState, roles: readonly string[]): Set<string> {
  const matrix = publishedMatrix(state);
  const result = new Set<string>();
  for (const roleCode of roles) {
    for (const permission of matrix?.roles.find((item) => item.roleCode === roleCode)
      ?.permissions ?? []) {
      result.add(permission);
    }
  }
  return result;
}

export function appendAudit(
  state: FakeAuthzState,
  event: Omit<
    AuditEventResponse,
    'id' | 'sequence' | 'hash' | 'previousHash' | 'correlationId' | 'occurredAt'
  > & {
    occurredAt?: string;
    correlationId?: string | null;
  },
): AuditEventResponse {
  state.sequence += 1;
  const previous = state.audit[state.audit.length - 1];
  const row: AuditEventResponse = {
    actorDisplayName: null,
    permission: null,
    ...event,
    id: fakeAuthzId(state),
    sequence: state.sequence,
    occurredAt: event.occurredAt ?? new Date().toISOString(),
    correlationId: event.correlationId ?? `corr-${state.sequence.toString().padStart(4, '0')}`,
    previousHash: previous?.hash ?? null,
    hash: `${state.sequence.toString(16).padStart(8, '0')}${'ab'.repeat(28)}`,
  };
  state.audit.push(row);
  return row;
}

export function seedAuthzState(me: MeResponse): FakeAuthzState {
  const base = new Date().toISOString();
  const today = todayUtc();
  const state: FakeAuthzState = {
    roles: ROLES.map((item) => ({ ...item, allowedScopeTypes: [...item.allowedScopeTypes] })),
    permissions: PERMISSIONS.map((item) => ({ ...item, classes: [...item.classes] })),
    matrixVersions: [],
    assignments: [],
    sodRules: [],
    breakGlass: [],
    campaigns: [],
    reviewItems: [],
    approvals: [],
    audit: [],
    base,
    counter: 0,
    sequence: 0,
  };

  state.matrixVersions.push({
    id: AUTHZ_IDS.matrixV1,
    version: 1,
    status: 'Published',
    effectiveFrom: shiftHours(base, -24 * 30),
    effectiveTo: null,
    publishedBy: null,
    secondKeyBy: null,
    changeSummary: 'Anexo A.3 — versão 1 semeada',
    twoKeyApprovalId: null,
    createdAt: shiftHours(base, -24 * 30),
    roles: MATRIX_V1.map((item) => ({
      roleCode: item.roleCode,
      permissions: [...item.permissions],
    })),
    rowVersion: 1,
  });

  const grant = (
    userId: string,
    roleCode: string,
    scopeType: string,
    scopeId: string | null,
    tenantId: string | null,
    options: {
      from?: string;
      to?: string;
      origin?: string;
      grantedBy?: string | null;
      seeded?: boolean;
    } = {},
  ): FakeAssignment => {
    const assignment: FakeAssignment = {
      id: fakeAuthzId(state),
      userId,
      roleCode,
      scopeType,
      scopeId,
      tenantId,
      effectiveFrom: options.from ?? shiftDays(today, -30),
      effectiveTo: options.to ?? null,
      origin: options.origin ?? 'Manual',
      reason: 'Atribuição semeada (modo fake)',
      grantedBy: options.grantedBy ?? null,
      grantedAt: shiftHours(base, -24 * 30),
      linkVerified: true,
      revokedBy: null,
      revokedAt: null,
      revokeReason: null,
      seeded: options.seeded ?? true,
    };
    state.assignments.push(assignment);
    return assignment;
  };

  // O próprio usuário de /me: um papel de rede por `networkAccess.roles` e um papel por `effectiveRoles` dos contextos.
  for (const roleCode of me.networkAccess.roles) {
    grant(me.user.id, roleCode, 'Network', null, null);
  }
  for (const context of me.contexts) {
    for (const effective of context.effectiveRoles) {
      grant(me.user.id, effective.role, effective.scopeType, effective.scopeId, context.tenantId);
    }
  }

  // Modo Greenfield de Produção (syllas@giyus.com.br): mantém apenas as atribuições full do administrador logado
  if (
    me.user.id === '01926f3e-0000-7000-8000-000000000099' ||
    me.user.email.toLowerCase() === 'syllas@giyus.com.br'
  ) {
    return state;
  }

  // Outras pessoas da rede e do centro-âncora (a própria pessoa de /me nunca é duplicada).
  if (me.user.id !== AUTHZ_IDS.userQuality) {
    grant(AUTHZ_IDS.userQuality, 'QUALITY_LEAD', 'Network', null, null);
  }
  if (me.user.id !== AUTHZ_IDS.userDpo) {
    grant(AUTHZ_IDS.userDpo, 'DPO', 'Network', null, null);
  }
  grant(AUTHZ_IDS.userInfosec, 'INFOSEC_LEAD', 'Network', null, null);

  // Versão 2 da matriz (GIY-056, E2E de publicação): rascunho para a QA (primeira chave) ou, para os demais principais,
  // já pendente da segunda chave com a primeira dada pela QA — o estado falso nasce por página, então a segunda pessoa
  // encontra a pendência semeada.
  const qaIsMe = me.networkAccess.roles.includes('QUALITY_LEAD');
  const v2Roles = MATRIX_V1.map((item) => ({
    roleCode: item.roleCode,
    permissions:
      item.roleCode === 'LOCAL_QUALITY'
        ? [...item.permissions, 'site.read'].sort()
        : [...item.permissions],
  }));
  const pendingApprovalId = qaIsMe ? null : fakeAuthzId(state);
  state.matrixVersions.push({
    id: AUTHZ_IDS.matrixDraft,
    version: 2,
    status: qaIsMe ? 'Draft' : 'PendingSecondKey',
    effectiveFrom: null,
    effectiveTo: null,
    publishedBy: qaIsMe ? null : AUTHZ_IDS.userQuality,
    secondKeyBy: null,
    changeSummary: 'Qualidade local passa a ler sites',
    twoKeyApprovalId: pendingApprovalId,
    createdAt: shiftHours(base, -2),
    roles: v2Roles,
    rowVersion: 1,
  });
  if (pendingApprovalId) {
    state.approvals.push({
      id: pendingApprovalId,
      actionType: 'authz.matrix.publish',
      objectType: 'RolePermissionMatrixVersion',
      objectId: AUTHZ_IDS.matrixDraft,
      status: 'PendingSecondKey',
      firstActorUserId: AUTHZ_IDS.userQuality,
      firstAt: shiftHours(base, -1),
      expiresAt: shiftHours(base, 71),
      secondActorUserId: null,
      secondAt: null,
      requiredSecondPermission: 'authz.matrix.publish',
      requireDistinctRole: true,
      firstActorRoles: ['QUALITY_LEAD'],
      payload: null,
    });
  }
  grant(
    AUTHZ_IDS.userPilotAdmin,
    'TENANT_ADMIN',
    'Tenant',
    FIXTURE_IDS.tenantPilot,
    FIXTURE_IDS.tenantPilot,
    {
      origin: 'Bootstrap',
    },
  );
  grant(
    AUTHZ_IDS.userPilotPi,
    'CLINICAL_REVIEWER',
    'Study',
    AUTHZ_IDS.studyPilot,
    FIXTURE_IDS.tenantPilot,
  );
  const scheduled = grant(
    AUTHZ_IDS.userPilotPi,
    'LOCAL_QUALITY',
    'Tenant',
    FIXTURE_IDS.tenantPilot,
    FIXTURE_IDS.tenantPilot,
    {
      from: shiftDays(today, 7),
    },
  );
  scheduled.reason = 'Começa na próxima semana';
  const revoked = grant(
    AUTHZ_IDS.userPilotAdmin,
    'SCHEDULER_RECEPTION',
    'Tenant',
    FIXTURE_IDS.tenantPilot,
    FIXTURE_IDS.tenantPilot,
  );
  revoked.revokedAt = shiftHours(base, -48);
  revoked.revokedBy = AUTHZ_IDS.userQuality;
  revoked.revokeReason = 'Função encerrada';

  state.sodRules.push(
    {
      id: AUTHZ_IDS.sodCalibration,
      kind: 'Act',
      leftCode: 'equipment.calibration.record',
      rightCode: 'equipment.calibration.verify',
      objectType: 'CalibrationRecord',
      description: 'Quem registra a calibração não a verifica (Parte 5.6).',
      status: 'Active',
      effectiveFrom: shiftDays(today, -30),
      effectiveTo: null,
      requestedBy: null,
      secondKeyBy: null,
      endedBy: null,
      endReason: null,
      twoKeyApprovalId: null,
      createdAt: shiftHours(base, -24 * 30),
      rowVersion: 1,
      pendingEnd: null,
    },
    {
      id: AUTHZ_IDS.sodQuality,
      kind: 'Assignment',
      leftCode: 'QUALITY_LEAD',
      rightCode: 'class:Operational',
      objectType: 'SameScope',
      description:
        'QA somente leitura estrutural: nunca acumula papel operacional no mesmo escopo.',
      status: 'Active',
      effectiveFrom: shiftDays(today, -30),
      effectiveTo: null,
      requestedBy: null,
      secondKeyBy: null,
      endedBy: null,
      endReason: null,
      twoKeyApprovalId: null,
      createdAt: shiftHours(base, -24 * 30),
      rowVersion: 1,
      pendingEnd: null,
    },
    {
      id: AUTHZ_IDS.sodDocument,
      kind: 'Act',
      leftCode: 'document.upload',
      rightCode: 'document.approve',
      objectType: 'DocumentVersion',
      description: 'Quem carrega a versão não a aprova.',
      status: 'Ended',
      effectiveFrom: shiftDays(today, -60),
      effectiveTo: shiftDays(today, -2),
      requestedBy: AUTHZ_IDS.userQuality,
      secondKeyBy: AUTHZ_IDS.userInfosec,
      endedBy: AUTHZ_IDS.userInfosec,
      endReason: 'Substituída pela regra do DOC (M7)',
      twoKeyApprovalId: null,
      createdAt: shiftHours(base, -24 * 60),
      rowVersion: 2,
      pendingEnd: null,
    },
  );

  state.breakGlass.push(
    {
      id: AUTHZ_IDS.breakGlassPending,
      userId: AUTHZ_IDS.userPilotPi,
      tenantId: FIXTURE_IDS.tenantPilot,
      scopeType: 'Study',
      scopeId: AUTHZ_IDS.studyPilot,
      reason: 'Participante em consulta de urgência; PI ausente e sem delegação vigente.',
      startedAt: shiftHours(base, -3),
      expiresAt: shiftHours(base, 1),
      endedAt: null,
      endReason: null,
      status: 'Active',
      reviewDueAt: shiftHours(base, 45),
      reviewedBy: null,
      reviewedAt: null,
      reviewOutcome: null,
      reviewNote: null,
      reviewOverdue: false,
    },
    {
      id: AUTHZ_IDS.breakGlassReviewed,
      userId: AUTHZ_IDS.userPilotPi,
      tenantId: FIXTURE_IDS.tenantPilot,
      scopeType: 'Study',
      scopeId: AUTHZ_IDS.studyPilot,
      reason: 'Auditoria interna do estudo com o coordenador de licença.',
      startedAt: shiftHours(base, -24 * 5),
      expiresAt: shiftHours(base, -24 * 5 + 4),
      endedAt: shiftHours(base, -24 * 5 + 4),
      endReason: 'expired',
      status: 'Ended',
      reviewDueAt: shiftHours(base, -24 * 3),
      reviewedBy: AUTHZ_IDS.userDpo,
      reviewedAt: shiftHours(base, -24 * 4),
      reviewOutcome: 'Justified',
      reviewNote: 'Uso compatível com a razão declarada.',
      reviewOverdue: false,
    },
  );

  const quarter = `${today.slice(0, 4)}-Q${String(Math.floor((Number(today.slice(5, 7)) - 1) / 3) + 1)}`;
  state.campaigns.push({
    id: AUTHZ_IDS.campaignOpen,
    quarter,
    status: 'Open',
    startedAt: shiftHours(base, -24 * 3),
    dueAt: shiftHours(base, 24 * 12),
    closedAt: null,
    itemCount: 2,
    decidedCount: 0,
    keptCount: 0,
    revokedCount: 0,
    escalatedCount: 0,
  });
  const pilotAdminAssignment = state.assignments.find(
    (a) => a.userId === AUTHZ_IDS.userPilotAdmin && a.roleCode === 'TENANT_ADMIN',
  );
  const infosecAssignment = state.assignments.find((a) => a.userId === AUTHZ_IDS.userInfosec);
  state.reviewItems.push(
    {
      id: AUTHZ_IDS.reviewItemMine,
      campaignId: AUTHZ_IDS.campaignOpen,
      reviewerUserId: me.user.id,
      subjectKind: 'RoleAssignment',
      subjectId: pilotAdminAssignment?.id ?? fakeAuthzId(state),
      subjectUserId: AUTHZ_IDS.userPilotAdmin,
      roleCode: 'TENANT_ADMIN',
      scopeType: 'Tenant',
      scopeId: FIXTURE_IDS.tenantPilot,
      tenantId: FIXTURE_IDS.tenantPilot,
      status: 'Pending',
      decision: null,
      decidedBy: null,
      decidedAt: null,
      decisionNote: null,
      escalatedAt: null,
      escalatedToUserId: null,
    },
    {
      id: AUTHZ_IDS.reviewItemOther,
      campaignId: AUTHZ_IDS.campaignOpen,
      reviewerUserId: AUTHZ_IDS.userQuality,
      subjectKind: 'RoleAssignment',
      subjectId: infosecAssignment?.id ?? fakeAuthzId(state),
      subjectUserId: AUTHZ_IDS.userInfosec,
      roleCode: 'INFOSEC_LEAD',
      scopeType: 'Network',
      scopeId: null,
      tenantId: null,
      status: 'Pending',
      decision: null,
      decidedBy: null,
      decidedAt: null,
      decisionNote: null,
      escalatedAt: null,
      escalatedToUserId: null,
    },
  );

  // Trilha semeada: alguns eventos de rede e do centro-âncora (com e sem estudo).
  appendAudit(state, {
    actorUserId: AUTHZ_IDS.userQuality,
    actorDisplayName: 'Quitéria Alves',
    actorKind: 'user',
    actorRoleSnapshot: '["QUALITY_LEAD"]',
    actorLinkSnapshot: null,
    tenantId: null,
    studySiteId: null,
    module: 'AUTHZ',
    eventType: 'AUTHZ.MATRIX_PUBLISHED',
    entityType: 'RolePermissionMatrixVersion',
    entityId: AUTHZ_IDS.matrixV1,
    before: null,
    after: '{"status":"Published","version":1}',
    changeReasonCode: null,
    changeReasonText: null,
    declaredPurpose: null,
    permission: 'authz.matrix.publish',
    occurredAt: shiftHours(base, -24 * 30),
  });
  appendAudit(state, {
    actorUserId: AUTHZ_IDS.userPilotAdmin,
    actorDisplayName: 'Marcos Lima',
    actorKind: 'user',
    actorRoleSnapshot: '["TENANT_ADMIN"]',
    actorLinkSnapshot: null,
    tenantId: FIXTURE_IDS.tenantPilot,
    studySiteId: null,
    module: 'TEN',
    eventType: 'TEN.SITE_UPDATED',
    entityType: 'Site',
    entityId: FIXTURE_IDS.sitePilotSede,
    before: '{"name":"Sede"}',
    after: '{"name":"Sede Central"}',
    changeReasonCode: 'DATA_CORRECTION',
    changeReasonText: 'Nome comercial atualizado',
    declaredPurpose: null,
    permission: 'site.manage',
    occurredAt: shiftHours(base, -24 * 2),
  });
  appendAudit(state, {
    actorUserId: AUTHZ_IDS.userPilotPi,
    actorDisplayName: 'Paula Investigadora',
    actorKind: 'user',
    actorRoleSnapshot: '["PRINCIPAL_INVESTIGATOR"]',
    actorLinkSnapshot: null,
    tenantId: FIXTURE_IDS.tenantPilot,
    studySiteId: AUTHZ_IDS.studyPilot,
    module: 'AUTHZ',
    eventType: 'AUTHZ.BREAK_GLASS_OPENED',
    entityType: 'BreakGlassEvent',
    entityId: AUTHZ_IDS.breakGlassPending,
    before: null,
    after: '{"scopeType":"Study"}',
    changeReasonCode: null,
    changeReasonText: null,
    declaredPurpose: null,
    permission: 'breakglass.use',
    occurredAt: shiftHours(base, -3),
  });
  appendAudit(state, {
    actorUserId: AUTHZ_IDS.userDpo,
    actorDisplayName: 'Dora Privacidade',
    actorKind: 'user',
    actorRoleSnapshot: '["DPO"]',
    actorLinkSnapshot: null,
    tenantId: FIXTURE_IDS.tenantPilot,
    studySiteId: null,
    module: 'AUTHZ',
    eventType: 'AUTHZ.PURPOSE_DECLARED',
    entityType: 'Tenant',
    entityId: FIXTURE_IDS.tenantPilot,
    before: null,
    after: '{"permission":"person.sensitive.read","purposeCode":"PrivacyRequest"}',
    changeReasonCode: null,
    changeReasonText: null,
    declaredPurpose: 'PrivacyRequest',
    permission: 'person.sensitive.read',
    occurredAt: shiftHours(base, -1),
  });
  appendAudit(state, {
    actorUserId: null,
    actorDisplayName: null,
    actorKind: 'system',
    actorRoleSnapshot: null,
    actorLinkSnapshot: null,
    tenantId: null,
    studySiteId: null,
    module: 'AUD',
    eventType: 'AUD.CHAIN_VERIFIED',
    entityType: 'AuditEvent',
    entityId: null,
    before: null,
    after: '{"eventsChecked":4,"isValid":true}',
    changeReasonCode: null,
    changeReasonText: null,
    declaredPurpose: null,
    permission: null,
    occurredAt: shiftHours(base, -0.5),
  });

  return state;
}
